/**
 * What a mosaic means, and nothing else.
 *
 * This file is the single definition of "solved" in Maritza's Mosaic. A picture
 * does not get to declare itself a fair puzzle — it hands its grid to
 * `countPictures` and is refused unless the answer is exactly one.
 *
 * That is the rule Stripey's Sudoku and Rocky's Riddles are already built on,
 * and it is here for the same reason: a puzzle that asserts itself is a puzzle
 * agreeing with itself while being wrong. A nonogram with two solutions is not
 * a hard nonogram, it is a broken one — and the child it breaks for is the one
 * who filled it in correctly and was told she had not.
 *
 * **Nothing that counts solutions runs while she plays.** Sudoku digs holes
 * and asks that question sixty times a puzzle, on her tablet, before she sees
 * anything; a mosaic's pictures are drawn by hand and fixed, so the only
 * caller of `countPictures` is the suite, checking each picture by name.
 * Correct rather than quick is a trade the search is allowed to make and
 * sudoku's was not.
 *
 * The rest of the file is on the hot path and knows it: `cluesFor` produces
 * the numbers she reads down the side, and `isPictureSolved` is asked after
 * every square she fills.
 *
 * **A picture is booleans.** Filled, or not. Which teddy it turns out to be is
 * decided in the browser and never reaches here — and neither does her third
 * cell state, the mark that says "I know this one is empty", because that is
 * her working rather than her answer. A mosaic is finished when the filled
 * squares match the clues, however she got there and whatever she pencilled in
 * on the way.
 */

/**
 * The grid sizes the game deals.
 *
 * Five is not big enough to be a teddy — twenty-five squares is a heart or a
 * fish — so the small ones are things from her teddies' world and a teddy
 * starts at ten. That is a fact about the picture set rather than about
 * solving, which is why nothing below this line mentions it: the solver works
 * on any size, and the test suite leans on that to build small awkward cases
 * by hand.
 */
export const MOSAIC_SIZES = [5, 10, 15] as const

export type MosaicSize = (typeof MOSAIC_SIZES)[number]

export function isMosaicSize(value: unknown): value is MosaicSize {
  return (MOSAIC_SIZES as readonly unknown[]).includes(value)
}

/** A picture, in row-major order. `picture[row * size + column]`. */
export type Picture = readonly boolean[]

/** The runs along one line, in order. A line with nothing filled is `[]`. */
export type MosaicClue = readonly number[]

/** What she is shown, and all she is shown. */
export interface Clues {
  readonly size: number
  readonly rows: readonly MosaicClue[]
  readonly columns: readonly MosaicClue[]
}

/** A filled square, as a picture is written down. */
export const FILLED = '#'

/** A square known to be empty. Not a mark she made — a square in the answer. */
export const BLANK = '.'

/**
 * A picture, read from the rows it is drawn as.
 *
 * Pictures are written as text so that a diff shows the teddy rather than four
 * hundred booleans, and so that redrawing one is redrawing it rather than
 * editing an array. That only works if the drawing is checked: a row of the
 * wrong length or a character that is neither `#` nor `.` is a slip of the
 * hand, and the alternative to refusing it is a square silently left empty and
 * a picture that no longer means what it looks like.
 */
export function pictureFrom(rows: readonly string[]): { picture: Picture; size: number } {
  const size = rows.length
  const squares: boolean[] = []

  rows.forEach((row, at) => {
    if (row.length !== size) {
      throw new Error(`Row ${at} is ${row.length} squares wide in a ${size}×${size} picture`)
    }

    for (const square of row) {
      if (square !== FILLED && square !== BLANK) {
        throw new Error(
          `Row ${at} has a '${square}' in it, which is neither '${FILLED}' nor '${BLANK}'`,
        )
      }

      squares.push(square === FILLED)
    }
  })

  return { picture: squares, size }
}

/** The filled runs along a line, which is what a clue is. */
function runsIn(cells: readonly boolean[]): number[] {
  const runs: number[] = []
  let run = 0

  for (const filled of cells) {
    if (filled) {
      run += 1
    } else if (run > 0) {
      runs.push(run)
      run = 0
    }
  }

  if (run > 0) runs.push(run)

  return runs
}

function rowOf(picture: Picture, size: number, row: number): boolean[] {
  return Array.from({ length: size }, (_, column) => picture[row * size + column] ?? false)
}

function columnOf(picture: Picture, size: number, column: number): boolean[] {
  return Array.from({ length: size }, (_, row) => picture[row * size + column] ?? false)
}

/**
 * The clues this picture would be drawn with.
 *
 * The one place runs are counted, so the numbers she reads down the side and
 * the numbers the solver works against cannot drift apart — the same mistake
 * `riddle-solve.ts` was written to prevent, where the code asking the question
 * and the code marking the answer agreed with each other and both were wrong.
 */
export function cluesFor(picture: Picture, size: number): Clues {
  if (picture.length !== size * size) {
    throw new Error(`A ${size}×${size} picture needs ${size * size} squares, not ${picture.length}`)
  }

  return {
    size,
    rows: Array.from({ length: size }, (_, row) => runsIn(rowOf(picture, size, row))),
    columns: Array.from({ length: size }, (_, column) => runsIn(columnOf(picture, size, column))),
  }
}

function sameRuns(left: readonly MosaicClue[], right: readonly MosaicClue[]): boolean {
  return (
    left.length === right.length &&
    left.every((runs, line) => {
      const other = right[line] ?? []

      return runs.length === other.length && runs.every((run, at) => run === other[at])
    })
  )
}

/**
 * Whether these filled squares finish this puzzle.
 *
 * Decided by the clues rather than by comparing against the picture the clues
 * came from. Those are the same answer only because every shipped picture is
 * checked unique, and saying it this way is what makes that check mean
 * something: if a picture ever had a second solution, a child who found it
 * would be told she was right, which is the honest outcome of shipping an
 * ambiguous puzzle rather than a second bug on top of it.
 */
export function isPictureSolved(filled: Picture, clues: Clues): boolean {
  const drawn = cluesFor(filled, clues.size)

  return sameRuns(drawn.rows, clues.rows) && sameRuns(drawn.columns, clues.columns)
}

/**
 * Every way one line could satisfy one clue.
 *
 * Cached, because a 15×15 asks for the same handful of clues over and over as
 * the search backtracks, and the widest of them has a few hundred answers.
 */
const arrangements = new Map<string, readonly (readonly boolean[])[]>()

function arrangementsFor(clue: MosaicClue, size: number): readonly (readonly boolean[])[] {
  const key = `${size}:${clue.join(',')}`
  const known = arrangements.get(key)

  if (known) return known

  const found: boolean[][] = []

  const build = (index: number, from: number, cells: readonly boolean[]): void => {
    if (index === clue.length) {
      found.push([...cells])

      return
    }

    const run = clue[index] ?? 0
    // Room for every run still to come, each needing a gap in front of it.
    let rest = 0
    for (let later = index + 1; later < clue.length; later += 1) rest += 1 + (clue[later] ?? 0)

    for (let start = from; start + run + rest <= size; start += 1) {
      const next = [...cells]
      for (let at = 0; at < run; at += 1) next[start + at] = true
      build(index + 1, start + run + 1, next)
    }
  }

  build(0, 0, new Array<boolean>(size).fill(false))
  arrangements.set(key, found)

  return found
}

/** How far down a column's clue we are: which run, and how much of it is laid. */
interface Progress {
  readonly index: number
  readonly open: number
}

/** One more square down a column, or null if the column has been made a lie. */
function step(clue: MosaicClue, at: Progress, filled: boolean): Progress | null {
  if (filled) {
    const target = clue[at.index]

    // More runs than the clue admits, or a run that has outgrown it.
    if (target === undefined || at.open + 1 > target) return null

    return { index: at.index, open: at.open + 1 }
  }

  if (at.open === 0) return at

  // A run has just ended, so it has to be exactly the length that was asked
  // for. It cannot be longer — that is refused above as it grows.
  if (at.open !== clue[at.index]) return null

  return { index: at.index + 1, open: 0 }
}

/**
 * Whether what is left of this column still fits in the rows that remain.
 *
 * This is the pruning that makes the search finish. Without it a 15×15 would
 * lay fifteen rows before noticing the first column went wrong at row two.
 *
 * It doubles as the finishing check: called with no rows left, "fits" can only
 * mean the column needs nothing more, which is to say every run it promised
 * has been laid.
 */
function stillFits(clue: MosaicClue, at: Progress, rowsLeft: number): boolean {
  let needed = 0

  if (at.open > 0) {
    needed = (clue[at.index] ?? 0) - at.open
    for (let later = at.index + 1; later < clue.length; later += 1) needed += 1 + (clue[later] ?? 0)
  } else {
    for (let later = at.index; later < clue.length; later += 1) {
      needed += (later === at.index ? 0 : 1) + (clue[later] ?? 0)
    }
  }

  return needed <= rowsLeft
}

/**
 * How many pictures these clues describe, up to `cap`.
 *
 * Stops at the cap, which is the whole point: a picture only ever needs "one,
 * or more than one", and an ambiguous 15×15 can have a great many.
 *
 * Row by row, because a row's possibilities come straight from its own clue —
 * there are a few hundred at worst, and they can be worked out once and
 * reused. What makes it finish is checking every column after each row rather
 * than at the end: a column that has already been contradicted stops the whole
 * branch there.
 */
export function countPictures(clues: Clues, cap = 2): number {
  const { size, rows, columns } = clues
  const perRow = rows.map((clue) => arrangementsFor(clue, size))

  let found = 0
  let progress: Progress[] = columns.map(() => ({ index: 0, open: 0 }))

  const place = (row: number): void => {
    if (found >= cap) return

    if (row === size) {
      found += 1

      return
    }

    const rowsLeft = size - row - 1

    for (const arrangement of perRow[row] ?? []) {
      const before = progress
      const next: Progress[] = []
      let ok = true

      for (let column = 0; column < size; column += 1) {
        const clue = columns[column] ?? []
        const moved = step(
          clue,
          before[column] ?? { index: 0, open: 0 },
          arrangement[column] ?? false,
        )

        if (!moved || !stillFits(clue, moved, rowsLeft)) {
          ok = false
          break
        }

        next.push(moved)
      }

      if (ok) {
        progress = next
        place(row + 1)
        progress = before
      }

      if (found >= cap) return
    }
  }

  place(0)

  return found
}

/**
 * The one picture these clues describe, or null if that is not how many.
 *
 * Asks for two and refuses to answer if it gets them, so nothing can end up
 * holding "a" solution to a puzzle that has several.
 */
export function solePicture(clues: Clues): boolean[] | null {
  if (countPictures(clues, 2) !== 1) return null

  const { size, rows, columns } = clues
  const picture: boolean[] = new Array<boolean>(size * size).fill(false)
  const perRow = rows.map((clue) => arrangementsFor(clue, size))

  let progress: Progress[] = columns.map(() => ({ index: 0, open: 0 }))

  const place = (row: number): boolean => {
    if (row === size) return true

    const rowsLeft = size - row - 1

    for (const arrangement of perRow[row] ?? []) {
      const before = progress
      const next: Progress[] = []
      let ok = true

      for (let column = 0; column < size; column += 1) {
        const clue = columns[column] ?? []
        const moved = step(
          clue,
          before[column] ?? { index: 0, open: 0 },
          arrangement[column] ?? false,
        )

        if (!moved || !stillFits(clue, moved, rowsLeft)) {
          ok = false
          break
        }

        next.push(moved)
      }

      if (!ok) continue

      progress = next
      for (let column = 0; column < size; column += 1) {
        picture[row * size + column] = arrangement[column] ?? false
      }

      if (place(row + 1)) return true

      progress = before
    }

    return false
  }

  return place(0) ? picture : null
}
